import MDAlert from "components/MDAlert";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import { JobStatus } from "generated/graphql";
import { useGetJobsForDropdown } from "hooks/jobs/useGetJobsForDropdown";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import { uniqBy } from "ramda";
import React, { useCallback } from "react";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";

interface JobOption {
  label: string;
  value: string | number;
  company?: any;
  proposalStage?: any;
  externalId?: string | null;
}

type JobSelectOptions = JobOption[];

const jobMap = (job): JobOption => ({
  label: `${job.proposalStage?.name || "No Stage"} - ${job.externalId || job.id}`,
  value: job.id,
  company: job.company,
  proposalStage: job.proposalStage,
  externalId: job.externalId,
});

export default function JobOptions({
  children,
  selected,
  queryOverride,
}: {
  children: ({
    jobOptions,
    loading,
    search,
    error,
    onLoadMore,
    hasMore,
  }: {
    jobOptions: JobSelectOptions;
    loading: boolean;
    search: (v: string) => void;
    error?: string;
    onLoadMore: () => void;
    hasMore: boolean;
  }) => React.ReactNode;
  selected?: JobOption;
  queryOverride?: { first?: number; statuses?: JobStatus[] };
}) {
  const { filtering } = usePaginatedVariables();
  const first = queryOverride?.first ?? 20;
  const statuses = queryOverride?.statuses ?? [
    JobStatus.TO_BE_SCHEDULED,
    JobStatus.SCHEDULED,
    JobStatus.IN_PROGRESS,
    JobStatus.WORK_COMPLETE,
    JobStatus.JOB_COSTING,
    JobStatus.READY_TO_BILL,
    JobStatus.COMPLETE,
  ];

  const { loading, data, error, paginatorInfo, fetchMore } = useGetJobsForDropdown({
    variables: {
      page: 1,
      first,
      search: filtering.debouncedSearchTerm,
      statuses,
    },
    skip: !filtering.debouncedSearchTerm?.trim(),
  });

  if (error) {
    return <MDAlert color="error">{parseGraphQLError(error)}</MDAlert>;
  }

  const jobs = data ?? [];

  const jobOptions: JobSelectOptions = selected
    ? uniqBy(
        (option) => option.value,
        [...jobs.map(jobMap), { ...selected, company: selected.company ?? null, proposalStage: selected.proposalStage ?? null }]
      )
    : jobs.map(jobMap);

  const onLoadMore = useCallback(() => {
    if (!paginatorInfo?.hasMorePages || loading) {
      return;
    }

    fetchMore({ page: paginatorInfo.currentPage + 1 });
  }, [fetchMore, loading, paginatorInfo]);

  return (
    <>
      {children({
        jobOptions,
        loading,
        search: filtering.setSearchTerm,
        error: undefined,
        onLoadMore,
        hasMore: paginatorInfo?.hasMorePages ?? false,
      })}
    </>
  );
}
